import React, { ReactNode, useState, useRef } from 'react';
import classNames from 'classnames';
import AnimateHeight, { Height } from 'react-animate-height';

import { IconChevronDown, IconChevronUp } from '@pentabd/icons';

import Text from '../text/Text';

import useOnClickOutside from '../../hooks/useOnClickOutside';

export interface MenuItemProps {
  disabled?: boolean;
  icon?: ReactNode;
  collapse?: boolean;
  name?: string;
  isMobile?: boolean;
  onClickMenu?: () => void;
  submenu?: {
    name: string;
    link?: string;
    icon?: ReactNode;
  }[];
  navItemSelected?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Link?: any;
  customClass?: {
    navItemColor?: string;
    navItemSelectedColor?: string;
    navItemHoverColor?: string;
    navItemTextColor?: string;
  };
  footerBorderColor?: string;
  testId?: string;
}

const MenuItem = ({
  disabled,
  icon,
  name,
  submenu = [],
  collapse,
  Link,
  isMobile,
  onClickMenu,
  customClass,
  navItemSelected,
  testId,
}: MenuItemProps) => {
  const ref = useRef(null);
  const [height, setHeight] = useState<Height | number>(0);
  const [subItemSelected, setSubItemSelected] = useState('');

  const handleClick = () => {
    if (disabled !== true) {
      setHeight(height === 0 ? 'auto' : 0);
    }
  };

  const handleSubItem = (subItem: string) => {
    setSubItemSelected(subItem);
  };

  const handleClickOutside = () => {
    setSubItemSelected('');
  };

  useOnClickOutside(ref, handleClickOutside);

  const TextHover = () => {
    if (customClass?.navItemSelectedColor) {
      return customClass.navItemSelectedColor;
    } else if (customClass?.navItemHoverColor) {
      return customClass.navItemHoverColor;
    } else if (customClass?.navItemTextColor) {
      return customClass.navItemTextColor;
    } else {
      return 'title';
    }
  };

  return (
    <div>
      <div className={classNames(customClass?.navItemHoverColor, 'menu-item')}>
        <div
          className={classNames(
            'd-flex justify-content-between w-100 py-4 px-6 w-100 rounded-2',
            {
              [customClass?.navItemColor]: navItemSelected != name,
              [customClass?.navItemSelectedColor]: navItemSelected == name,
            },
            {
              pointer: disabled !== true,
            },
          )}
          onClick={handleClick}
        >
          <span className="d-flex align-items-center gap-6">
            {icon ? (
              <span className="menu-item-icon d-flex align-items-center justify-content-center">
                {icon}
              </span>
            ) : null}
            {name ? (
              <Text
                weight="semibold"
                color={TextHover()}
                className={classNames({ 'd-none': !isMobile && !collapse })}
              >
                {name}
              </Text>
            ) : null}
          </span>
          {submenu.length ? (
            <div
              className={classNames('d-flex align-items-center', {
                'd-none': !isMobile && !collapse,
              })}
            >
              {height == 'auto' ? (
                <IconChevronUp size="24" stroke="subtitle2" />
              ) : (
                <IconChevronDown size="24" stroke="subtitle2" />
              )}
            </div>
          ) : null}
        </div>
      </div>
      <AnimateHeight
        id={name}
        duration={500}
        height={height}
        className="list-style-none"
      >
        {submenu.length
          ? submenu.map(
              (item) =>
                item?.name && (
                  <Link
                    data-testid={`${testId}-${item.name}`}
                    to={item.link}
                    key={item.name}
                    className={classNames(
                      'text-decoration-none',
                      {
                        'd-none': !isMobile && !collapse,
                      },
                      customClass?.navItemHoverColor,
                    )}
                    ref={ref}
                  >
                    <li
                      className={classNames(
                        'menu-item-subitems w-100 mt-2 py-6 pointer rounded-2',
                        {
                          [customClass?.navItemSelectedColor]:
                            subItemSelected == item.name,
                          [customClass?.navItemColor]:
                            subItemSelected != item.name,
                        },
                      )}
                      onClick={() => {
                        isMobile && onClickMenu(), handleSubItem(item.name);
                      }}
                    >
                      <span className="d-flex align-items-center gap-6">
                        {item.icon ? (
                          <span className="menu-item-icon d-flex align-items-center justify-content-center">
                            {item.icon}
                          </span>
                        ) : null}
                        <Text weight="semibold" color={TextHover()}>
                          {item.name}
                        </Text>
                      </span>
                    </li>
                  </Link>
                ),
            )
          : null}
      </AnimateHeight>
    </div>
  );
};
export default MenuItem;
